import React, { useState } from "react";
import {
  SIZES, MOULDINGS, GLAZINGS, MATS, DEFAULT_SELECTION,
  resolveSelection, priceBreakdown, formatEuros,
} from "../lib/marketing/pricing";

// Marketing configurator island (M11): pick size / moulding / glazing / mat and watch a
// CSS-composed framed preview and a live EUR breakdown update — powered entirely by the
// client-side mock model (no Framing API, nothing gated). It lives on /try only, so the
// rest of the marketing site stays zero-JS.

// Rendered even on the selected chip so the Paid-tier signal never disappears; the on-dark
// variant reads against the selected chip's slate-900 background.
function PaidBadge({ onDark = false }) {
  return (
    <span
      className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide align-middle ${
        onDark ? "bg-white text-slate-900" : "bg-slate-900 text-white"
      }`}
    >
      Paid
    </span>
  );
}

// A single-select option group backed by real radio inputs (kept visually hidden), so it
// carries native radio-group semantics and keyboard behaviour for assistive tech; the
// visible chip is styled from React state.
function OptionGroup({ name, legend, options, selectedId, onSelect, renderOption }) {
  return (
    <fieldset>
      <legend className="text-sm font-semibold text-slate-900">{legend}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.id === selectedId;
          return (
            <label
              key={option.id}
              className={`flex cursor-pointer items-center rounded-md border px-3 py-2 text-sm transition-colors focus-within:ring-2 focus-within:ring-cyan-500 ${
                selected
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"
              }`}
            >
              <input
                type="radio"
                name={name}
                value={option.id}
                checked={selected}
                onChange={() => onSelect(option.id)}
                className="sr-only"
              />
              {renderOption ? renderOption(option, selected) : option.label}
              {option.paid && <PaidBadge onDark={selected} />}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

// The framed preview: a frame (moulding tone) around an optional mat around the artwork,
// with a faint sheen when museum glass is selected. Pure CSS — no image request.
function FramedPreview({ moulding, mat, glazing, ratio }) {
  const hasMat = mat.id !== "none";
  return (
    <div className="flex items-center justify-center">
      <div
        className="w-full max-w-[300px] shadow-2xl"
        style={{ aspectRatio: String(ratio), backgroundColor: moulding.tone, padding: "6%" }}
      >
        <div className="relative h-full w-full" style={{ backgroundColor: hasMat ? "#f7f5f0" : "transparent", padding: hasMat ? "11%" : 0 }}>
          <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-cyan-100 via-slate-200 to-cyan-200">
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-medium uppercase tracking-wider text-slate-500">Your artwork</span>
            </div>
            {glazing.id === "museum" && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{ background: "linear-gradient(120deg, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 40%)" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MarketingConfigurator({ registerPath = "/register" }) {
  const [selection, setSelection] = useState(DEFAULT_SELECTION);
  const resolved = resolveSelection(selection);
  const { lines, vat, total } = priceBreakdown(selection);
  const update = (key) => (id) => setSelection((prev) => ({ ...prev, [key]: id }));

  return (
    <div className="grid gap-10 lg:grid-cols-2 lg:items-start">
      {/* Preview */}
      <div className="lg:sticky lg:top-24">
        <FramedPreview moulding={resolved.moulding} mat={resolved.mat} glazing={resolved.glazing} ratio={resolved.size.ratio} />
        <p className="mt-4 text-center text-sm text-slate-500">
          {resolved.size.label} · {resolved.moulding.label} · {resolved.glazing.label}
          {resolved.mat.id !== "none" ? ` · ${resolved.mat.label}` : ""}
        </p>
      </div>

      {/* Controls + price */}
      <div className="space-y-6">
        <OptionGroup name="size" legend="Size" options={SIZES} selectedId={selection.size} onSelect={update("size")} />
        <OptionGroup
          name="moulding"
          legend="Moulding"
          options={MOULDINGS}
          selectedId={selection.moulding}
          onSelect={update("moulding")}
          renderOption={(option) => (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-sm ring-1 ring-black/10" style={{ backgroundColor: option.tone }} aria-hidden="true" />
              {option.label}
            </span>
          )}
        />
        <OptionGroup name="glazing" legend="Glazing" options={GLAZINGS} selectedId={selection.glazing} onSelect={update("glazing")} />
        <OptionGroup name="mat" legend="Mat" options={MATS} selectedId={selection.mat} onSelect={update("mat")} />

        {/* Live price breakdown — announced to assistive tech when a selection changes. */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" role="status" aria-live="polite" aria-atomic="true">
          <dl className="space-y-2 text-sm">
            {lines.map(([label, cents]) => (
              <div key={label} className="flex justify-between border-b border-dashed border-slate-200 pb-2">
                <dt className="text-slate-600">{label}</dt>
                <dd className="text-slate-900">{formatEuros(cents)}</dd>
              </div>
            ))}
            <div className="flex justify-between pb-2">
              <dt className="text-slate-600">VAT (21% LV)</dt>
              <dd className="text-slate-900">{formatEuros(vat)}</dd>
            </div>
            <div className="flex justify-between pt-2 text-base font-bold">
              <dt>You pay, delivered</dt>
              <dd>{formatEuros(total)}</dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-slate-500">
            Illustrative price — no per-order markup. You'll see your exact cost in the configurator before you sell anything.
          </p>
          <a
            href={registerPath}
            className="mt-5 inline-block w-full rounded-md bg-slate-900 px-5 py-3 text-center text-base font-semibold text-white hover:bg-slate-700"
          >
            Get early access to sell this
          </a>
        </div>
      </div>
    </div>
  );
}
